import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {isDateWithinDays, formatISODateToMMDDYYYY, formatCurrency} from '../../utils/common'
import PlanBadge, {getPlanType} from './PlanBadge'
import ExpiryWarning from './ExpiryWarning'
import LeftoverAmount from './LeftoverAmount'
import BalanceProgressBar from './BalanceProgressBar'
import type {TpaTrackerProps} from './types'

const TpaTracker: React.FC<TpaTrackerProps> = ({
    trackerData,
    activeCurrency,
    balanceTrackerExpiryDays,
    popupData,
    onDetailsClick,
    showDetailsButton = true
}) => {
    const intl = useIntl()

    // Get all plans with expiry dates within the threshold
    const plansWithUpcomingExpiry = useMemo(() => {
        if (!balanceTrackerExpiryDays || !popupData) {
            return []
        }
        return popupData
            .filter((plan) => {
                if (!plan.expiryDate) return false
                const formattedDate = formatISODateToMMDDYYYY(plan.expiryDate)
                return formattedDate && isDateWithinDays(formattedDate, balanceTrackerExpiryDays)
            })
            .map((plan) => ({
                planType: getPlanType(plan.accountName),
                balance: formatCurrency(plan.balance, intl, activeCurrency),
                deadlineDate: formatISODateToMMDDYYYY(plan.expiryDate)
            }))
    }, [balanceTrackerExpiryDays, popupData, intl, activeCurrency])

    const hasUpcomingExpiry = plansWithUpcomingExpiry.length > 0

    const currentAvailableBalance = parseFloat(trackerData?.currentAvailableBalance) || 0
    const availableAmount = formatCurrency(currentAvailableBalance, intl, activeCurrency)

    const balanceUsagePercentageNumber = parseFloat(trackerData?.balanceUsagePercentage) || 0

    const usedSessionBalance = parseFloat(trackerData?.usedSessionBalance) || 0
    const leftToSpend = currentAvailableBalance - usedSessionBalance
    const leftToSpendAmount = formatCurrency(leftToSpend, intl, activeCurrency)

    const leftoverOrderAmountInt = parseFloat(trackerData?.leftoverOrderAmount) || 0
    const leftoverOrderAmount =
        leftoverOrderAmountInt > 0
            ? formatCurrency(leftoverOrderAmountInt, intl, activeCurrency)
            : null

    const isLeftoverAmountValid = !isNaN(leftoverOrderAmountInt) && leftoverOrderAmountInt > 0

    // Determine if user has multiple plans
    const isMultiPlan = popupData && popupData.length > 1

    return (
        <div className="relative w-full pt-4 pb-[0.9375rem] text-emphasis bg-transparent after:absolute after:left-0 after:top-0 after:z-[-1] after:w-full after:h-full after:bg-gray-01 after:opacity-80">
            {/* Header row: Title and Details link */}
            <div className="flex justify-between items-center mb-2">
                <button
                    type="button"
                    className="text-lg font-medium text-gray-700 hover:cursor-pointer"
                    onClick={onDetailsClick}
                >
                    Balance Display™
                </button>
                {showDetailsButton && onDetailsClick && (
                    <button
                        onClick={onDetailsClick}
                        className="text-xs font-medium underline hover:no-underline whitespace-nowrap"
                        style={{color: '#004968'}}
                        data-testid="tracker-DetailsLink"
                    >
                        Details
                    </button>
                )}
            </div>

            {/* Progress bar row with balance on right */}
            <BalanceProgressBar
                percentage={balanceUsagePercentageNumber}
                leftToSpend={leftToSpendAmount}
                available={availableAmount}
                showAsterisk={isMultiPlan && hasUpcomingExpiry}
                showAvailableToSpend={true}
                onClick={onDetailsClick}
            />

            {/* Plan bubbles for multi-plan users */}
            {isMultiPlan && popupData && (
                <div className="flex flex-col gap-2 mt-3 w-full">
                    <div className="flex flex-col md:flex-row gap-2 w-full md:justify-between md:items-center">
                        <div className="badge_container flex flex-row gap-2 items-center w-full md:w-auto">
                            {popupData.map((plan) => (
                                <PlanBadge
                                    key={`${plan.accountName}-${plan.expiryDate || 'no-expiry'}`}
                                    accountName={plan.accountName}
                                    balance={plan.balance}
                                    expiryDate={plan.expiryDate}
                                    currency={activeCurrency}
                                />
                            ))}
                            {/* Expiry warnings for all plans with upcoming deadlines - inline on desktop */}
                            <div className="hidden md:flex md:flex-row md:gap-2">
                                {plansWithUpcomingExpiry.map((plan) => (
                                    <ExpiryWarning
                                        key={`${plan.planType}-${plan.deadlineDate}`}
                                        planType={plan.planType}
                                        balance={plan.balance}
                                        deadlineDate={plan.deadlineDate}
                                        showPlanType={isMultiPlan}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="amount_container hidden md:block">
                            {/* Leftover amount message (when cart > balance) - on desktop */}
                            {isLeftoverAmountValid && leftoverOrderAmount && (
                                <LeftoverAmount amount={leftoverOrderAmount} />
                            )}
                        </div>
                    </div>
                    {/* Expiry warnings row - below badges on mobile only */}
                    {plansWithUpcomingExpiry.length > 0 && (
                        <div className="flex md:hidden flex-row gap-2 w-full">
                            {plansWithUpcomingExpiry.map((plan) => (
                                <ExpiryWarning
                                    key={`${plan.planType}-${plan.deadlineDate}-mobile`}
                                    planType={plan.planType}
                                    balance={plan.balance}
                                    deadlineDate={plan.deadlineDate}
                                    showPlanType={isMultiPlan}
                                />
                            ))}
                        </div>
                    )}
                    {/* Leftover amount message - below expiry warnings on mobile only */}
                    {isLeftoverAmountValid && leftoverOrderAmount && (
                        <div className="amount_container md:hidden w-full">
                            <LeftoverAmount amount={leftoverOrderAmount} />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default TpaTracker
